<template>
  <div class="simple-line-chart" ref="chartContainer" @mouseleave="handleMouseLeave">
    <svg :viewBox="`0 0 ${width} ${height}`" preserveAspectRatio="none" @mousemove="handleMouseMove">
      <!-- 网格线 -->
      <g class="grid-lines">
        <line
          v-for="i in 2"
          :key="'h-'+i"
          :x1="padding.left"
          :y1="padding.top + ((i-1) * (chartHeight / 1))"
          :x2="width - padding.right"
          :y2="padding.top + ((i-1) * (chartHeight / 1))"
          stroke="#eee"
          stroke-width="0.5"
          stroke-dasharray="4,2"/>
        <line
          v-for="i in 7"
          :key="'v-'+i"
          :x1="padding.left + ((i-1) * (chartWidth / 6))"
          :y1="padding.top"
          :x2="padding.left + ((i-1) * (chartWidth / 6))"
          :y2="height - padding.bottom"
          stroke="#eee"
          stroke-width="0.5"
          stroke-dasharray="4,2"/>
      </g>

      <!-- Y轴刻度 (只显示最大值) -->
      <g class="y-axis">
        <text
          :key="'yt-top'"
          :x="padding.left + 5"
          :y="padding.top + 6"
          fill="#999"
          font-size="12"
          text-anchor="start">
          {{ formatMaxYLabel(effectiveYMax) }}{{ yUnit }}
        </text>
      </g>

      <!-- X轴刻度 -->
      <g class="x-axis">
        <text
          v-for="(item, index) in displayData"
          :key="'xt-'+index"
          v-show="shouldShowXLabel(index)"
          :x="padding.left + (index * (chartWidth / Math.max(displayData.length - 1, 1)))"
          :y="height - 8"
          fill="#999"
          font-size="12"
          text-anchor="middle">
          {{ formatDate(item[xKey]) }}
        </text>
      </g>

      <!-- 折线区域 -->
      <defs>
        <linearGradient :id="'gradient-' + chartId" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" :style="`stop-color:${color};stop-opacity:0.3`" />
          <stop offset="100%" :style="`stop-color:${color};stop-opacity:0.05`" />
        </linearGradient>
      </defs>

      <!-- 填充区域 -->
      <path
        v-if="displayData.length > 1"
        :d="areaPath"
        :fill="'url(#gradient-' + chartId + ')'" />

      <!-- 折线 -->
      <polyline
        v-if="displayData.length > 1"
        :points="linePoints"
        fill="none"
        :stroke="color"
        stroke-width="1"
        stroke-linecap="round"
        stroke-linejoin="round"/>

      <!-- 鼠标悬停: 垂直标线 + 数据点 + tooltip -->
      <g v-if="hoverIndex !== null">
        <!-- 垂直标线 -->
        <line
          :x1="getX(hoverIndex)"
          :y1="padding.top"
          :x2="getX(hoverIndex)"
          :y2="height - padding.bottom"
          stroke="#999"
          stroke-width="0.5"
          stroke-dasharray="3,2"/>
        <!-- 数据点高亮 -->
        <circle
          :cx="getX(hoverIndex)"
          :cy="getY(displayData[hoverIndex][yKey])"
          r="3"
          fill="#fff"
          :stroke="color"
          stroke-width="1.5"/>
        <!-- Tooltip背景 -->
        <rect
          :x="tooltipX"
          :y="tooltipY"
          :width="tooltipWidth"
          :height="48"
          rx="4"
          fill="rgba(0,0,0,0.75)"/>
        <!-- Tooltip: 日期 -->
        <text
          :x="tooltipX + 8"
          :y="tooltipY + 18"
          fill="#fff"
          font-size="12">
          {{ displayData[hoverIndex][xKey] }}
        </text>
        <!-- Tooltip: 金额 -->
        <text
          :x="tooltipX + 8"
          :y="tooltipY + 36"
          fill="#fff"
          font-size="12"
          font-weight="bold">
          Amount: {{ formatAmount(displayData[hoverIndex][yKey]) }}{{ yUnit }}
        </text>
      </g>
    </svg>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { filter } from '@libs/utils'
export default {
  name: 'SimpleLineChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    xKey: {
      type: String,
      default: 'date'
    },
    yKey: {
      type: String,
      default: 'value'
    },
    yMax: {
      type: Number,
      default: 0
    },
    yUnit: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: '#5B8FF9'
    },
    width: {
      type: Number,
      default: 300
    },
    height: {
      type: Number,
      default: 150
    }
  },
  setup(props) {
    const chartId = Math.random().toString(36).substr(2, 9)

    const padding = {
      top: 28,
      right: 16,
      bottom: 36,
      left: 16
    }

    const hoverIndex = ref(null)
    const svgRef = ref(null)

    const chartWidth = computed(() => props.width - padding.left - padding.right)
    const chartHeight = computed(() => props.height - padding.top - padding.bottom)

    const displayData = computed(() => {
      return props.data
    })

    // 计算数据中的最大金额
    const maxDataAmount = computed(() => {
      if (!displayData.value.length) return 0
      return Math.max(...displayData.value.map(item => item[props.yKey] || 0))
    })

    // 实际使用的yMax: 取数据最大值和传入yMax的较大值
    const effectiveYMax = computed(() => {
      const fromData = maxDataAmount.value || 1
      // 如果传入的yMax <= 0 (默认值), 则使用数据最大值; 否则取较大值
      return props.yMax > 0 ? Math.max(props.yMax, fromData) : fromData
    })

    const getX = (index) => {
      if (displayData.value.length <= 1) return padding.left
      return padding.left + (index * (chartWidth.value / (displayData.value.length - 1)))
    }

    const getY = (value) => {
      const max = effectiveYMax.value || 1
      const normalizedValue = Math.min(value, max)
      return padding.top + chartHeight.value - (normalizedValue / max * chartHeight.value)
    }

    const linePoints = computed(() => {
      return displayData.value.map((item, index) => {
        return `${getX(index)},${getY(item[props.yKey])}`
      }).join(' ')
    })

    const areaPath = computed(() => {
      if (displayData.value.length <= 1) return ''
      const points = displayData.value.map((item, index) => {
        return `${getX(index)},${getY(item[props.yKey])}`
      })
      const startX = getX(0)
      const endX = getX(displayData.value.length - 1)
      return `M ${startX} ${props.height - padding.bottom} ` +
             `L ${points.join(' L ')} ` +
             `L ${endX} ${props.height - padding.bottom} Z`
    })

    const formatDate = (date) => {
      if (!date) return ''
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date.substring(5)
      }
      const d = new Date(date)
      return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }

    const formatMaxYLabel = (val) => {
      // if (val >= 1000) {
      //   return (val / 1000).toFixed(1) + 'k'
      // }
      return filter.currency(val, '', '', 'amount')
    }

    const formatAmount = (val) => {
      if (val === 0) return '0.00'
      if (val >= 1000) {
        return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }
      return Number(val).toFixed(2)
    }

    // 鼠标移动时, 找到最近的数据点
    const handleMouseMove = (e) => {
      if (!displayData.value.length) return
      const svg = e.target.ownerSVGElement
      if (!svg) return
      const rect = svg.getBoundingClientRect()
      // 计算SVG坐标系中的x (使用viewBox映射)
      const svgX = ((e.clientX - rect.left) / rect.width) * props.width

      // 找到最近的数据点索引
      let closestIndex = 0
      let closestDist = Infinity
      for (let i = 0; i < displayData.value.length; i++) {
        const pointX = getX(i)
        const dist = Math.abs(svgX - pointX)
        if (dist < closestDist) {
          closestDist = dist
          closestIndex = i
        }
      }
      hoverIndex.value = closestIndex
    }

    const handleMouseLeave = () => {
      hoverIndex.value = null
    }

    // Tooltip位置计算
    const tooltipWidth = 180
    const tooltipX = computed(() => {
      if (hoverIndex.value === null) return 0
      const x = getX(hoverIndex.value) + 10
      // 防止超出右边界
      if (x + tooltipWidth > props.width - padding.right) {
        return getX(hoverIndex.value) - tooltipWidth - 10
      }
      return x
    })
    const tooltipY = computed(() => {
      if (hoverIndex.value === null) return 0
      // 显示在数据点上方
      const pointY = getY(displayData.value[hoverIndex.value][props.yKey])
      return Math.max(padding.top, pointY - 56)
    })

    const shouldShowXLabel = (index) => {
      const total = displayData.value.length
      if (total <= 12) {
        return true
      }
      const targetCount = 12
      const step = Math.ceil(total / targetCount)
      return index === 0 || index === total - 1 || index % step === 0
    }

    return {
      chartId,
      padding,
      chartWidth,
      chartHeight,
      displayData,
      getX,
      getY,
      linePoints,
      areaPath,
      formatDate,
      formatMaxYLabel,
      formatAmount,
      shouldShowXLabel,
      hoverIndex,
      tooltipX,
      tooltipY,
      tooltipWidth,
      handleMouseMove,
      handleMouseLeave,
      effectiveYMax
    }
  }
}
</script>

<style scoped>
.simple-line-chart {
  width: 100%;
  height: 100%;
}

.simple-line-chart svg {
  width: 100%;
  height: 100%;
  cursor: crosshair;
}
</style>
